import React, { useEffect, useState, useRef } from 'react';
import './Hero.css';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BellRing,
  Star,
  Users,
  Zap,
  Check,
  X,
  Trophy,
  Flame,
  Gift
} from 'lucide-react';
import BlogCarousel from './BlogCarousel';

function useScrollReveal() {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setRevealed(true); observer.disconnect(); } },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, revealed];
}

function Hero() {
  const [isVisible, setIsVisible] = useState(false);
  const [wordIndex, setWordIndex] = useState(0);

  const [versusRef, versusVisible] = useScrollReveal();
  const [featuresRef, featuresVisible] = useScrollReveal();
  const [gamiRef, gamiVisible] = useScrollReveal();
  const [reviewsRef, reviewsVisible] = useScrollReveal();

  const dynamicWords = [
    "ta pause déj'.",
    "les flux étudiants.",
    "ton temps.",
    "l'affluence au RU."
  ];

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % dynamicWords.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [dynamicWords.length]);

  return (
    <div className="landing-wrapper">
      {/* 1. HERO */}
      <section className="hero-large">
        <div className="hero-large-container">
          <div className={`hero-large-content ${isVisible ? 'fade-in-up' : ''}`}>
            <h1 className="hero-super-title">
              La solution pour optimiser<br />
              <div className="dynamic-text-wrapper text-highlight">
                <span className="dynamic-word" key={wordIndex}>
                  {dynamicWords[wordIndex]}
                </span>
              </div>
            </h1>
            <p className="hero-super-desc">
              L'application qui transforme l'expérience CROUS. Les étudiants anticipent
              l'affluence en temps réel et gagnent du temps, pendant que les établissements
              régulent leurs flux et évitent la surcharge.
            </p>
            <div className="hero-large-actions">
              <a
                href="https://apps.apple.com/fr/app/crousradar/id6755202143"
                className="btn-brutal primary"
              >
                Télécharger l'App (Gratuit) <ArrowRight size={20} />
              </a>
              <Link to="/etablissement" className="btn-brutal secondary">
                Espace Établissements
              </Link>
            </div>

            <div className="social-proof-brutal">
              <div className="avatar-stack">
                <img src="/student_avatar_1_1774986237326.png" alt="" />
                <img src="/student_avatar_2_1774986250615.png" alt="" />
                <div className="avatar-more">+5k</div>
              </div>
              <div className="social-text-brutal">
                Déjà plus de 5000 étudiants nous font confiance chaque jour.
              </div>
            </div>
          </div>

          <div className={`hero-large-visual ${isVisible ? 'fade-in-left' : ''}`}>
            <div className="photo-collage">
              <img src="/friends_laughing_1774987258418.png" className="collage-img img-main" alt="" />
              <img src="/student_phone_1774987368756.png" className="collage-img img-side" alt="" />
              <img src="/candid_cafeteria_1774987203013.png" className="collage-img img-bottom" alt="" />
              <div className="brutal-badge b-1">🔥 Mabillon Blindé !</div>
              <div className="brutal-badge b-2">✅ Port-Royal Vide</div>
            </div>
          </div>
        </div>
      </section>

      {/* 1b. ILS PARLENT DE NOUS — press logos */}
      <section className="press-section">
        <div className="press-inner">
          <span className="press-label">Dans les médias</span>
          <div className="press-logos">
            <a
              href="https://www.ouest-france.fr/education/etudiant/un-calvaire-pour-manger-lasse-de-faire-la-queue-au-resto-universitaire-ce-breton-lance-une-appli-pour-y-remedier-c4a007f2-c254-11f1-a107-88e926908e5f"
              target="_blank"
              rel="noopener noreferrer"
              className="press-logo-link"
              aria-label="Article Ouest-France"
            >
              <img src="/logos/ouest-france.png" alt="Ouest-France" className="press-logo" />
            </a>
            <a
              href="https://www.rcf.fr/sud-bretagne"
              target="_blank"
              rel="noopener noreferrer"
              className="press-logo-link"
              aria-label="RCF Sud Bretagne"
            >
              <img src="/logos/rcf.png" alt="RCF Radio" className="press-logo" />
            </a>
          </div>
        </div>
      </section>

      {/* 2. LE DÉCLIC — problème → solution */}
      <section className={`versus-section scroll-reveal ${versusVisible ? 'revealed' : ''}`} ref={versusRef}>
        <div className="section-container">
          <div className="section-intro">
            <span className="kicker">LE DÉCLIC</span>
            <h2 className="section-title">
              Deux pauses déj.<br />
              <span className="section-title-accent">Un monde d'écart.</span>
            </h2>
            <p className="section-lead">
              Le RU n'est pas la galère. C'est le manque d'information qui l'est.
            </p>
          </div>

          <div className="versus-grid">
            <div className="versus-card versus-before">
              <div className="versus-tag versus-tag-bad">AVANT</div>
              <h3>Tu arrives au RU à 12h30…</h3>
              <ul className="versus-list">
                <li><X size={18} /> 20 minutes debout dans la file.</li>
                <li><X size={18} /> Ton plat préféré est déjà épuisé.</li>
                <li><X size={18} /> Ta pause déj passe à la trappe.</li>
                <li><X size={18} /> Tu finis sur un sandwich à 6 € d'à côté.</li>
              </ul>
              <div className="versus-mood">😤</div>
            </div>

            <div className="versus-divider">
              <span>VS</span>
            </div>

            <div className="versus-card versus-after">
              <div className="versus-tag versus-tag-good">AVEC CROUSRADAR</div>
              <h3>Tu ouvres l'app.</h3>
              <ul className="versus-list">
                <li><Check size={18} strokeWidth={3} /> Affluence en direct, avant de partir.</li>
                <li><Check size={18} strokeWidth={3} /> Tu décales de 15 min, file quasi-vide.</li>
                <li><Check size={18} strokeWidth={3} /> Repas chaud à 1 €, complet.</li>
                <li><Check size={18} strokeWidth={3} /> Tu gagnes des points au passage.</li>
              </ul>
              <div className="versus-mood">🤌</div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. FEATURES — les 3 piliers */}
      <section className="features-modern" ref={featuresRef}>
        <div className="section-container">
          <div className={`section-intro ft-reveal ${featuresVisible ? 'ft-visible' : ''}`}>
            <span className="kicker">LES ESSENTIELS</span>
            <h2 className="section-title">Trois outils. <span className="section-title-accent">Une seule mission.</span></h2>
            <p className="section-lead">
              CrousRadar donne aux étudiants l'information qu'ils n'avaient jamais eue :
              savoir quand partir, où aller, et combien de temps attendre.
            </p>
          </div>
          <div className="features-cards">
            <div className={`feature-card fc-dark ft-reveal ft-from-left ${featuresVisible ? 'ft-visible' : ''}`}>
              <div className="fc-header">
                <div className="fc-icon-ring"><BellRing size={28} /></div>
                <span className="fc-num">01</span>
              </div>
              <span className="fc-tag">Temps réel</span>
              <h3>Alertes en direct</h3>
              <p>Reçois une notification dès que l'affluence baisse dans ton restaurant favori.</p>
            </div>
            <div className={`feature-card fc-orange ft-reveal ft-from-bottom ${featuresVisible ? 'ft-visible' : ''}`}>
              <div className="fc-header">
                <div className="fc-icon-ring"><Star size={28} /></div>
                <span className="fc-num">02</span>
              </div>
              <span className="fc-tag">Gamification</span>
              <h3>Gagne des points</h3>
              <p>Signale l'affluence à la communauté et débloque des récompenses exclusives.</p>
            </div>
            <div className={`feature-card fc-outline ft-reveal ft-from-right ${featuresVisible ? 'ft-visible' : ''}`}>
              <div className="fc-header">
                <div className="fc-icon-ring"><Users size={28} /></div>
                <span className="fc-num">03</span>
              </div>
              <span className="fc-tag">Collaboratif</span>
              <h3>100% Communautaire</h3>
              <p>Basé sur la solidarité étudiante : plus on est nombreux, plus l'info est fiable !</p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. GAMIFICATION — la récompense */}
      <section className="gamification" ref={gamiRef}>
        <div className="section-container">
          <div className="gamification-grid">
            <div className={`gamification-text gami-reveal gami-from-left ${gamiVisible ? 'gami-visible' : ''}`}>
              <span className="kicker">PLAY & RANK</span>
              <h2 className="gamification-title">
                Plus tu aides, <span className="gamification-highlight">plus tu montes</span>.
              </h2>
              <p className="gamification-desc">
                Chaque signalement te rapporte de l'XP et te fait grimper dans le classement.
                Défie tes amis, débloque des badges et deviens le #1 de ton campus.
              </p>
              <div className="gamification-features">
                <div className={`gamification-feature gami-reveal gami-from-bottom ${gamiVisible ? 'gami-visible' : ''}`} style={{transitionDelay: '0.3s'}}>
                  <div className="gamification-feature-icon"><Zap size={20} /></div>
                  <div>
                    <strong>+50 XP</strong>
                    <span>par signalement validé</span>
                  </div>
                </div>
                <div className={`gamification-feature gami-reveal gami-from-bottom ${gamiVisible ? 'gami-visible' : ''}`} style={{transitionDelay: '0.45s'}}>
                  <div className="gamification-feature-icon"><Trophy size={20} /></div>
                  <div>
                    <strong>Classement live</strong>
                    <span>global & entre amis</span>
                  </div>
                </div>
                <div className={`gamification-feature gami-reveal gami-from-bottom ${gamiVisible ? 'gami-visible' : ''}`} style={{transitionDelay: '0.6s'}}>
                  <div className="gamification-feature-icon"><Flame size={20} /></div>
                  <div>
                    <strong>Streaks</strong>
                    <span>maintiens ta série</span>
                  </div>
                </div>
                <div className={`gamification-feature gami-reveal gami-from-bottom ${gamiVisible ? 'gami-visible' : ''}`} style={{transitionDelay: '0.75s'}}>
                  <div className="gamification-feature-icon"><Gift size={20} /></div>
                  <div>
                    <strong>Récompenses réelles</strong>
                    <span>les meilleurs décrochent un cookie, un dessert ou une boisson offerte au CROUS</span>
                  </div>
                </div>
              </div>
            </div>

            <div className={`gamification-visual gami-reveal gami-from-right ${gamiVisible ? 'gami-visible' : ''}`}>
              <div className="gamification-phone-container">
                <div className="gamification-glow" aria-hidden="true" />
                <img
                  src="/leaderboard-screen.png"
                  alt="Leaderboard CrousRadar"
                  className="gamification-phone"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. AVIS — carrousel témoignages */}
      <section className="reviews-section" ref={reviewsRef}>
        <div className="section-container">
          <div className={`section-intro rv-reveal ${reviewsVisible ? 'rv-visible' : ''}`}>
            <span className="kicker">LA COMMUNAUTÉ</span>
            <h2 className="section-title">Ce qu'ils en <span className="section-title-accent">pensent.</span></h2>
          </div>

          <div className={`reviews-carousel rv-reveal rv-from-bottom ${reviewsVisible ? 'rv-visible' : ''}`}>
            <div className="reviews-track">
              {[...Array(2)].flatMap((_, dup) => [
                { text: "Je check CrousRadar avant même de quitter l'amphi. Ça m'évite 20 min de queue.", name: "Julien M.", role: "L3 Droit, Paris-Saclay", stars: 5 },
                { text: "Le système de points est addictif. Je signale à chaque pause pour grimper dans le classement !", name: "Sarah T.", role: "Master IAE, Lyon", stars: 5 },
                { text: "Devenu indispensable. Ma promo entière l'utilise depuis la rentrée.", name: "Inès L.", role: "L2 Sciences, Bordeaux", stars: 5 },
                { text: "Enfin une app pensée par et pour les étudiants. Simple, fiable, gratuite.", name: "Amine B.", role: "Master Info, Toulouse", stars: 5 },
                { text: "Grâce aux alertes, j'arrive pile quand c'est vide. Game changer.", name: "Léa D.", role: "L1 Éco, Nantes", stars: 5 },
                { text: "On est 200+ sur le leaderboard de notre campus. C'est devenu un délire de promo.", name: "Maxime R.", role: "L3 STAPS, Montpellier", stars: 5 },
              ].map((review, i) => (
                <div className="review-card" key={`${dup}-${i}`}>
                  <div className="review-stars">{'★'.repeat(review.stars)}</div>
                  <p className="review-text">"{review.text}"</p>
                  <div className="review-author">
                    <div className="review-avatar">{review.name.charAt(0)}</div>
                    <div>
                      <strong>{review.name}</strong>
                      <span>{review.role}</span>
                    </div>
                  </div>
                </div>
              )))}
            </div>
          </div>
          <p className={`reviews-disclaimer rv-reveal ${reviewsVisible ? 'rv-visible' : ''}`} style={{transitionDelay: '0.4s'}}>* Prénoms modifiés, avis recueillis anonymement auprès d'utilisateurs.</p>
        </div>
      </section>

      {/* 6. ACTU — contenu éditorial juste avant le footer */}
      <BlogCarousel />
    </div>
  );
}

export default Hero;
